import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Collapse,
  Flex,
  message,
  Modal,
  Typography,
} from "antd";
import {
  TruckOutlined,
  CloseCircleTwoTone,
  ArrowLeftOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { axiosInstance, formatPrice, nombreDeEntrega } from "@/utilities";
import { API_URL_ORDERS } from "@/services/XCONS_ENV";
import {
  DiscountPrice,
  getUnitFinalInclFromDescription,
} from "@/components/discount-icon";
import noImage from "@/assets/img/no-img.png";
import envioImage from "@/assets/img/img-envio.png";

const { Text, Title } = Typography;
const cents = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

type BulkGroup = {
  groupId: string;
  pendingShipments: any[];
  canceledShipments: any[];
  stage: "preview" | "committed";
};

type Props = {
  /**
   * Todos los shipments del order. El panel computa internamente cuáles
   * son los "afectados" según el modo.
   */
  items: any[];
  productsMap: Record<string, any>;
  orderId?: number;
  updateOrderData?: () => void;
  /**
   * - "select": click reciente al menu, aún no se hizo Request en backend.
   * - "preview": Request ya hizo POST y los shipments están en pending_cancel.
   * - "committed": Confirm hecho, shipments canceled con groupId compartido.
   */
  mode: "select" | "preview" | "committed";
  /** Info del grupo bulk si ya hay uno en backend. En modo 'select' es undef. */
  bulkGroup?: BulkGroup;
  /** Callback para volver al listado normal (Back desde 'select'). */
  onExitSelectMode?: () => void;
};

/**
 * Panel que reemplaza la lista normal de shipments cuando se está en el
 * flow de cancelación masiva. Renderiza una lista plana con todos los
 * items de TODOS los pending shipments (orden por shipment, sin unificar
 * repetidos), incluyendo la línea de envío de cada delivery.
 *
 * Etapas:
 *   - select: items todos preselected, header `Back / Request cancellation`.
 *   - preview: items en read-only con label "Cancel", header `Reject request / Confirm cancellation`.
 *   - committed: vista "All pending deliveries cancelled" — collapse cerrado
 *                con el resumen del grupo cancelado.
 */
export const BulkCancelPendingPanel: React.FC<Props> = ({
  items,
  productsMap,
  orderId,
  updateOrderData,
  mode,
  bulkGroup,
  onExitSelectMode,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  // Shipments afectados según modo. En select: todos los pending. En
  // preview/committed: los del grupo (pending_cancel o canceled
  // respectivamente).
  const affectedShipments = useMemo(() => {
    if (mode === "select") {
      return (items ?? []).filter((s: any) => s?.status === "pending");
    }
    if (mode === "preview" && bulkGroup) {
      return bulkGroup.pendingShipments;
    }
    if (mode === "committed" && bulkGroup) {
      return bulkGroup.canceledShipments;
    }
    return [];
  }, [items, mode, bulkGroup]);

  // Lista plana de items: orden por shipment, sin unificar repetidos.
  // Cada entry mantiene una referencia al shipment de origen para etiquetar.
  const flatItems = useMemo(() => {
    const out: Array<{
      shipment: any;
      item: any;
      shipmentIndex: number;
    }> = [];
    affectedShipments.forEach((sh: any, sIdx: number) => {
      (sh?.items ?? []).forEach((it: any) => {
        out.push({ shipment: sh, item: it, shipmentIndex: sIdx });
      });
    });
    return out;
  }, [affectedShipments]);

  // grandTotal combinado: shipping de cada shipment + items × precio.
  const grandTotal = useMemo(() => {
    let total = 0;
    for (const sh of affectedShipments) {
      total += Number(sh?.priceInclTax) || 0;
      for (const it of sh?.items ?? []) {
        const product = productsMap?.[it.orderItemId];
        const unit = product ? getUnitFinalInclFromDescription(product) : 0;
        total += unit * (Number(it.qty) || 0);
      }
    }
    return cents(total);
  }, [affectedShipments, productsMap]);

  const productLabel = (orderItemId: number) => {
    const product = productsMap?.[orderItemId];
    return (
      product?.linkedProduct?.name ||
      product?.name ||
      `Item #${orderItemId}`
    );
  };

  const productSubtitle = (orderItemId: number) => {
    const product = productsMap?.[orderItemId];
    return (
      product?.linkedProduct?.variations?.[0]?.mainCatalog?.name ||
      product?.variations?.[0]?.mainCatalog?.name ||
      ""
    );
  };

  const productImage = (orderItemId: number) => {
    const product = productsMap?.[orderItemId];
    const linked = product?.linkedProduct;
    return (
      linked?.variations?.[0]?.mainCatalog?.image ||
      product?.variations?.[0]?.mainCatalog?.image ||
      noImage
    );
  };

  // ------ HANDLERS ------
  const handleRequest = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      await axiosInstance.post(
        `${API_URL_ORDERS}/${orderId}/cancel-pending/request`,
        { grandTotal },
      );
      message.success("Solicitud de cancelación masiva registrada");
      // Salir del modo 'select' local — el backend ahora dicta el estado
      // (bulkGroupInfo derived).
      onExitSelectMode?.();
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          e?.message ||
          "Error al registrar la solicitud",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!orderId || !bulkGroup) return;
    setLoading(true);
    try {
      await axiosInstance.post(
        `${API_URL_ORDERS}/${orderId}/cancel-pending/reject`,
        { groupId: bulkGroup.groupId },
      );
      message.success("Solicitud rechazada");
      onExitSelectMode?.();
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          e?.message ||
          "Error al rechazar la solicitud",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    Modal.confirm({
      title: t("orders.bulkCancel.confirm.title"),
      content: t("orders.bulkCancel.confirm.content"),
      okText: t("orders.bulkCancel.confirm.okText"),
      cancelText: t("buttom.back"),
      icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
      okButtonProps: { danger: true, type: "primary" },
      cancelButtonProps: { icon: <ArrowLeftOutlined /> },
      onOk: async () => {
        if (!orderId || !bulkGroup) return;
        setLoading(true);
        try {
          await axiosInstance.post(
            `${API_URL_ORDERS}/${orderId}/cancel-pending/confirm`,
            { groupId: bulkGroup.groupId },
          );
          message.success("Cancelación masiva confirmada");
          updateOrderData?.();
        } catch (e: any) {
          message.error(
            e?.response?.data?.message ||
              e?.message ||
              "Error al confirmar la cancelación",
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // ------ COMMITTED: "All pending deliveries cancelled" ------
  if (mode === "committed") {
    return (
      <Collapse
        defaultActiveKey={[`bulk-${bulkGroup?.groupId}`]}
        className="custom-header-collapse-canceled"
        style={{ marginBottom: 16 }}
        items={[
          {
            key: `bulk-${bulkGroup?.groupId}`,
            label: (
              <Flex align="center" justify="space-between" style={{ width: "100%" }}>
                <Flex align="center" gap={8}>
                  <TruckOutlined style={{ color: "white", fontSize: 16 }} />
                  <Text strong style={{ color: "white" }}>
                    {t("orders.bulkCancel.committedTitle")}
                  </Text>
                </Flex>
                <CloseCircleTwoTone twoToneColor={"red"} style={{ transform: "scale(1.5)" }} />
              </Flex>
            ),
            children: (
              <Flex vertical gap={8}>
                {affectedShipments.map((sh: any, sIdx: number) => (
                  <Card
                    key={`canceled-sh-${sh.shipmentId}`}
                    title={
                      <Text strong>
                        {nombreDeEntrega(sh?.metadata?.name) ||
                          sh?.deliveryMethod ||
                          t("orders.shipment.deliveryN", { n: sIdx + 1 })}
                      </Text>
                    }
                    style={{ background: "#fff2f0" }}
                  >
                    {/* Línea de envío del shipment */}
                    <Flex
                      align="center"
                      justify="space-between"
                      style={{
                        paddingBottom: 12,
                        marginBottom: 12,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Flex gap={12} align="center">
                        <img
                          src={envioImage}
                          style={{ width: 60, objectFit: "cover" }}
                          alt="Shipping"
                        />
                      </Flex>
                      <Flex vertical style={{ flex: 1, marginLeft: 16 }}>
                        <Text strong>
                          {nombreDeEntrega(sh?.metadata?.name) ||
                            sh?.deliveryMethod ||
                            t("orders.shipment.shipping")}
                        </Text>
                      </Flex>
                      <Flex vertical align="end" style={{ width: 120 }}>
                        <Text>1</Text>
                      </Flex>
                      <Flex align="end" style={{ minWidth: 100 }}>
                        <Title style={{ margin: 0 }} level={5}>
                          {Number(sh?.priceInclTax) === 0
                            ? t("orders.shipment.free")
                            : formatPrice(Number(sh?.priceInclTax) || 0)}
                        </Title>
                      </Flex>
                    </Flex>

                    {/* Items del shipment */}
                    {(sh?.items ?? []).map((it: any, iIdx: number) => {
                      const product = productsMap?.[it.orderItemId];
                      const unit = product
                        ? getUnitFinalInclFromDescription(product)
                        : 0;
                      const qty = Number(it.qty) || 0;
                      const lineTotal = cents(unit * qty);
                      return (
                        <Flex
                          key={`canceled-it-${sh.shipmentId}-${it.orderItemId}-${iIdx}`}
                          align="center"
                          justify="space-between"
                          style={{
                            paddingBottom: 8,
                            marginBottom: 8,
                            borderBottom:
                              iIdx !== (sh?.items?.length ?? 0) - 1
                                ? "1px solid #f0f0f0"
                                : undefined,
                          }}
                        >
                          <Flex gap={12} align="center">
                            <img
                              src={productImage(it.orderItemId)}
                              style={{ width: 60, objectFit: "cover" }}
                              alt={productLabel(it.orderItemId)}
                            />
                          </Flex>
                          <Flex vertical style={{ flex: 1, marginLeft: 16 }}>
                            <Text strong>
                              {productLabel(it.orderItemId)}
                            </Text>
                            {productSubtitle(it.orderItemId) && (
                              <Text italic>
                                {productSubtitle(it.orderItemId)}
                              </Text>
                            )}
                          </Flex>
                          <Flex vertical align="end" style={{ width: 120 }}>
                            <Flex align="center" gap={6}>
                              <Text style={{ color: "#ff4d4f" }}>{t("orders.shipment.cancel")}</Text>
                              <Text strong>{qty}</Text>
                            </Flex>
                          </Flex>
                          <Flex align="end" style={{ minWidth: 100 }}>
                            <Title style={{ margin: 0 }} level={5}>
                              {formatPrice(lineTotal)}
                            </Title>
                          </Flex>
                        </Flex>
                      );
                    })}
                  </Card>
                ))}
                {/* Total combinado */}
                <Flex
                  style={{
                    backgroundColor: "#DAE8E0",
                    padding: "8px 16px",
                    borderRadius: "8px",
                  }}
                  justify="end"
                  align="baseline"
                  gap={60}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    {t("orders.shipment.total")}
                  </Title>
                  <Title level={5} style={{ margin: 0 }}>
                    {formatPrice(grandTotal)}
                  </Title>
                </Flex>
              </Flex>
            ),
          },
        ]}
      />
    );
  }

  // ------ SELECT / PREVIEW: lista plana editable / readonly ------
  return (
    <Collapse
      defaultActiveKey={["bulk-cancel-pending"]}
      className="custom-header-collapse"
      style={{ marginBottom: 16 }}
      items={[
        {
          key: "bulk-cancel-pending",
          label: (
            <Flex align="center" gap={8}>
              <TruckOutlined style={{ color: "white", fontSize: 16 }} />
              <Text strong style={{ color: "white" }}>
                {t("orders.bulkCancel.panelTitle")}
              </Text>
            </Flex>
          ),
          children: (
            <Flex vertical gap={12}>
      {/* Header con botones — Back a la izquierda con ArrowLeft, acción primaria a la derecha */}
      <Flex align="center" justify="space-between">
        {mode === "select" ? (
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => onExitSelectMode?.()}
            disabled={loading}
          >
            {t("buttom.back")}
          </Button>
        ) : mode === "preview" ? (
          <Button
            icon={<ArrowLeftOutlined />}
            loading={loading}
            onClick={handleReject}
          >
            {t("buttom.back")}
          </Button>
        ) : (
          <span />
        )}
        {mode === "select" && (
          <Button
            danger
            loading={loading}
            onClick={handleRequest}
          >
            {t("orders.shipment.requestCancellation")}
          </Button>
        )}
        {mode === "preview" && (
          <Button
            type="primary"
            danger
            loading={loading}
            onClick={handleConfirm}
          >
            {t("orders.shipment.confirmCancellation")}
          </Button>
        )}
      </Flex>

      {/* Select all en modo `select` está oculto (todos están preseleccionados
          y no se desmarca). Solo se mantiene en preview por si lo necesitamos
          a futuro. */}

      {/* Lista plana de items (incluye línea de envío al inicio de cada shipment) */}
      <Card
        className="tarjeta-encabezado-32"
        style={{ background: "#fff2f0" }}
        title={
          <Text strong style={{ fontSize: 14 }}>
            {t("orders.shipment.productsToBeCancelled")}
          </Text>
        }
      >
        {affectedShipments.map((sh: any, sIdx: number) => {
          const shippingIncl = Number(sh?.priceInclTax) || 0;
          const shippingName =
            nombreDeEntrega(sh?.metadata?.name) ||
            sh?.deliveryMethod ||
            t("orders.shipment.deliveryN", { n: sIdx + 1 });
          return (
            <React.Fragment key={`bulk-sh-${sh.shipmentId}`}>
              {/* Header de la entrega */}
              <Flex
                align="center"
                style={{
                  padding: "8px 12px",
                  background: "rgba(0,0,0,0.03)",
                  marginTop: sIdx === 0 ? 0 : 16,
                  marginBottom: 8,
                  borderRadius: 4,
                }}
              >
                <Text strong>
                  {t("orders.shipment.deliveryWithName", {
                    n: sIdx + 1,
                    name: shippingName,
                  })}
                </Text>
              </Flex>

              {/* Fila de envío */}
              <Flex
                align="center"
                justify="space-between"
                style={{
                  paddingBottom: 12,
                  marginBottom: 12,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Flex gap={12} align="center">
                  <img
                    src={envioImage}
                    style={{ width: 60, objectFit: "cover" }}
                    alt="Shipping"
                  />
                </Flex>
                <Flex vertical style={{ flex: 1, marginLeft: 16, minWidth: 0 }}>
                  <Text strong>{shippingName}</Text>
                </Flex>
                {/* Bloque derecho: precio unitario + cancel/qty + subtotal,
                    separados con gap para que no queden apretados. */}
                <Flex align="center" gap={48} style={{ flexShrink: 0 }}>
                  <DiscountPrice shipment={sh} align="end" />
                  <Flex
                    align="center"
                    justify="center"
                    gap={6}
                    style={{ minWidth: 100 }}
                  >
                    <Text style={{ color: "#ff4d4f" }}>{t("orders.shipment.cancel")}</Text>
                    <Text strong>1</Text>
                  </Flex>
                  <Flex align="end" justify="end" style={{ minWidth: 110 }}>
                    <Title style={{ margin: 0 }} level={5}>
                      {shippingIncl === 0 ? t("orders.shipment.free") : formatPrice(shippingIncl)}
                    </Title>
                  </Flex>
                </Flex>
              </Flex>

              {/* Items del shipment */}
              {(sh?.items ?? []).map((it: any, iIdx: number) => {
                const product = productsMap?.[it.orderItemId];
                const unit = product
                  ? getUnitFinalInclFromDescription(product)
                  : 0;
                const qty = Number(it.qty) || 0;
                const lineTotal = cents(unit * qty);
                return (
                  <Flex
                    key={`bulk-it-${sh.shipmentId}-${it.orderItemId}-${iIdx}`}
                    align="center"
                    justify="space-between"
                    style={{
                      paddingBottom: 12,
                      marginBottom: 12,
                      borderBottom:
                        iIdx !== (sh?.items?.length ?? 0) - 1
                          ? "1px solid #f0f0f0"
                          : undefined,
                    }}
                  >
                    <Flex gap={12} align="center">
                          <img
                        src={productImage(it.orderItemId)}
                        style={{ width: 60, objectFit: "cover" }}
                        alt={productLabel(it.orderItemId)}
                      />
                    </Flex>
                    <Flex
                      vertical
                      style={{ flex: 1, marginLeft: 16, minWidth: 0 }}
                    >
                      <Text strong>{productLabel(it.orderItemId)}</Text>
                      {productSubtitle(it.orderItemId) && (
                        <Text italic>{productSubtitle(it.orderItemId)}</Text>
                      )}
                    </Flex>
                    {/* Bloque derecho: precio unitario + cancel/qty + subtotal,
                        separados con gap para que no queden apretados. */}
                    <Flex align="center" gap={48} style={{ flexShrink: 0 }}>
                      {product && <DiscountPrice item={product} align="end" />}
                      <Flex
                        align="center"
                        justify="center"
                        gap={6}
                        style={{ minWidth: 100 }}
                      >
                        <Text style={{ color: "#ff4d4f" }}>{t("orders.shipment.cancel")}</Text>
                        <Text strong>{qty}</Text>
                      </Flex>
                      <Flex align="end" justify="end" style={{ minWidth: 110 }}>
                        <Title style={{ margin: 0 }} level={5}>
                          {formatPrice(lineTotal)}
                        </Title>
                      </Flex>
                    </Flex>
                  </Flex>
                );
              })}
            </React.Fragment>
          );
        })}
        {/* Total */}
        <Flex
          style={{
            marginTop: 8,
            backgroundColor: "#DAE8E0",
            padding: "8px 16px",
            borderRadius: "8px",
          }}
          justify="end"
          align="baseline"
          gap={60}
        >
          <Title level={5} style={{ margin: 0 }}>
            {t("orders.shipment.total")}
          </Title>
          <Title level={5} style={{ margin: 0 }}>
            {formatPrice(grandTotal)}
          </Title>
        </Flex>
      </Card>
            </Flex>
          ),
        },
      ]}
    />
  );
};
